import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { showLocalNotification } from "@/lib/push";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function useNotificationSocket(user) {
  const qc = useQueryClient();
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const connect = () => {
      const token = localStorage.getItem("leadify_token");
      if (!token) return;
      const wsUrl = BACKEND.replace(/^http/, "ws") + `/api/ws/notifications?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => { retryRef.current = 0; };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.event === "notification" && msg.notification) {
            const n = msg.notification;
            qc.invalidateQueries({ queryKey: ["notif-count"] });
            qc.invalidateQueries({ queryKey: ["notifs"] });
            qc.invalidateQueries({ queryKey: ["inbox-unread"] });
            // in-app toast
            toast(n.title, { description: n.body, action: n.link ? { label: "Open", onClick: () => (window.location.href = n.link) } : undefined });
            // browser notification (if permission granted and tab hidden or blurred)
            if (document.hidden || !document.hasFocus()) {
              showLocalNotification(n.title, n.body, n.link);
            }
          } else if (msg.event === "inbox_message") {
            // Real-time refresh: new message arrived in some conversation
            qc.invalidateQueries({ queryKey: ["convs"] });
            qc.invalidateQueries({ queryKey: ["inbox-unread"] });
            if (msg.conversation_id) {
              qc.invalidateQueries({ queryKey: ["conv", msg.conversation_id] });
            }
          }
        } catch {}
      };
      ws.onclose = () => {
        if (cancelled) return;
        const delay = Math.min(30000, 1000 * Math.pow(2, retryRef.current));
        retryRef.current += 1;
        timerRef.current = setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };

    connect();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [user?.id, qc]);
}
